import { useEffect, useState } from "react";
import { authObj } from "@/firebase-config";
import { resolveEffectiveTenantId } from "@/utils/fmb-outstanding-broadcast";

/**
 * Whether a tenant-scoped feature is enabled for the effective tenant
 * (JWT `tenantId`, or `localStorage.impersonatedTenantId` for super-admin).
 */
export function useTenantFeatureGate(
  isEnabledForTenant: (tenantId: string | null | undefined) => boolean
): boolean {
  const [enabled, setEnabled] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    const impersonated = localStorage.getItem("impersonatedTenantId");
    return isEnabledForTenant(
      resolveEffectiveTenantId({ impersonatedTenantId: impersonated, jwtTenantId: null })
    );
  });

  useEffect(() => {
    let cancelled = false;

    const sync = async () => {
      const impersonated =
        typeof localStorage !== "undefined" ? localStorage.getItem("impersonatedTenantId") : null;
      let jwtTenantId: string | null = null;

      if (!impersonated?.trim()) {
        const user = authObj.currentUser;
        if (user) {
          try {
            const token = await user.getIdTokenResult();
            jwtTenantId = typeof token.claims.tenantId === "string" ? token.claims.tenantId : null;
          } catch {
            jwtTenantId = null;
          }
        }
      }

      if (cancelled) return;
      setEnabled(
        isEnabledForTenant(
          resolveEffectiveTenantId({ impersonatedTenantId: impersonated, jwtTenantId })
        )
      );
    };

    void sync();
    return () => {
      cancelled = true;
    };
  }, [isEnabledForTenant]);

  return enabled;
}
